/**
 * Phase 9 — 💳 Coin Purchase Return Flow
 * --------------------------------------
 * Stripe sends the user to /coins/return?coins_session={id} after
 * checkout. This page polls /api/coins/status/{id} until the wallet is
 * credited (idempotent on the backend), then shows a confirmation card
 * with the coins added, fresh balance, and quick links back to Wallet /
 * Home / Purchase History.
 *
 * UX states (each with a clear visual + data-testid):
 *   - "polling"    purchase confirmed by Stripe but not yet credited
 *   - "credited"   coins now in the wallet — show confirmation
 *   - "canceled"   user backed out of Stripe — show cancel state
 *   - "timeout"    we polled 12x and Stripe still hasn't confirmed
 *   - "error"      404 / network — show retry
 */
import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api from "@/lib/api";
import SharedNavigation from "@/components/SharedNavigation";
import FirstGiftNudge from "@/components/FirstGiftNudge";

const POLL_INTERVAL_MS = 2000;
const MAX_POLLS = 12; // ~24s

const CoinReturnPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [phase, setPhase] = useState("loading");  // loading | polling | credited | canceled | timeout | error
  const [purchase, setPurchase] = useState(null);  // { coins, bonus_coins, total_coins, pack_name, amount_usd }
  const [wallet, setWallet] = useState(null);
  const [me, setMe] = useState(null);
  const [pollCount, setPollCount] = useState(0);
  const stoppedRef = useRef(false);

  const refreshMe = async () => {
    try {
      const r = await api.get("/auth/me");
      setMe(r.data || null);
    } catch (_e) {}
  };

  const refreshWallet = async () => {
    try {
      const r = await api.get("/wallet/me");
      setWallet(r.data?.wallet || null);
    } catch (_e) {}
  };

  useEffect(() => {
    refreshMe();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sess = params.get("coins_session");
    const canceled = params.get("coins_canceled");

    if (canceled) {
      setPhase("canceled");
      return;
    }
    if (!sess) {
      setPhase("error");
      return;
    }
    setPhase("polling");

    let attempts = 0;
    const tick = async () => {
      if (stoppedRef.current) return;
      attempts += 1;
      setPollCount(attempts);
      try {
        const r = await api.get(`/coins/status/${sess}`);
        const data = r.data || {};
        if (data.credited) {
          setPurchase({
            coins: data.coins,
            bonus_coins: data.bonus_coins,
            total_coins: data.total_coins,
            pack_name: data.pack_name,
            amount_usd: data.amount_usd,
          });
          await refreshWallet();
          stoppedRef.current = true;
          setPhase("credited");
          toast.success(`🎉 +${(data.total_coins || data.coins || 0).toLocaleString()} coins added!`);
          return;
        }
      } catch (e) {
        if (e?.response?.status === 404) {
          stoppedRef.current = true;
          setPhase("error");
          return;
        }
        // Network blip — keep polling.
      }
      if (attempts < MAX_POLLS) {
        setTimeout(tick, POLL_INTERVAL_MS);
      } else {
        stoppedRef.current = true;
        setPhase("timeout");
      }
    };
    tick();
    return () => { stoppedRef.current = true; };
  }, [location.search]);

  return (
    <>
      <Helmet>
        <title>Coin Purchase | RGM Studio</title>
      </Helmet>
      <div className="min-h-screen bg-[#08080c]">
        <SharedNavigation currentUser={me} activePage="wallet" />
        <div className="max-w-lg mx-auto px-4 pt-24 pb-12" data-testid="coin-return-page">
          {(phase === "polling" || phase === "loading") && (
            <Card className="p-6 sm:p-8 bg-gradient-to-br from-purple-500/10 to-pink-500/10 border-purple-500/30 text-center" data-testid="coin-return-polling">
              <div className="text-5xl mb-3">⏳</div>
              <h1 className="text-2xl font-black text-white mb-2">Confirming your purchase…</h1>
              <p className="text-pink-200/80 text-sm mb-4">
                Stripe is finalizing payment. This usually takes just a moment.
              </p>
              <div className="h-2 rounded-full bg-white/5 overflow-hidden mb-1">
                <div
                  className="h-full bg-gradient-to-r from-purple-400 to-pink-400 transition-all duration-500"
                  style={{ width: `${Math.min(100, (pollCount / MAX_POLLS) * 100)}%` }}
                  data-testid="coin-return-progress"
                />
              </div>
              <p className="text-pink-200/50 text-[10px] mt-2 uppercase tracking-wider">
                Attempt {pollCount}/{MAX_POLLS}
              </p>
            </Card>
          )}

          {phase === "credited" && (
            <Card className="p-6 sm:p-8 bg-gradient-to-br from-emerald-500/15 to-amber-500/10 border-emerald-400/40 text-center" data-testid="coin-return-credited">
              <div className="text-6xl mb-3">🎉</div>
              <h1 className="text-3xl font-black text-white mb-1">Coins added!</h1>
              <p className="text-emerald-200/80 text-sm mb-5">
                Your wallet has been topped up. Ready to go support some artists.
              </p>

              <div className="bg-black/30 rounded-xl p-4 mb-5 border border-white/10 text-left space-y-2" data-testid="coin-return-summary">
                {purchase?.pack_name && (
                  <div className="flex justify-between text-sm">
                    <span className="text-pink-200/60">Pack</span>
                    <span className="text-white font-semibold">{purchase.pack_name}</span>
                  </div>
                )}
                {purchase?.amount_usd != null && (
                  <div className="flex justify-between text-sm">
                    <span className="text-pink-200/60">Charged</span>
                    <span className="text-white font-semibold">${Number(purchase.amount_usd).toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-pink-200/60">Coins</span>
                  <span className="text-amber-200 font-bold">+{(purchase?.coins || 0).toLocaleString()}</span>
                </div>
                {purchase?.bonus_coins ? (
                  <div className="flex justify-between text-sm">
                    <span className="text-pink-200/60">Bonus</span>
                    <span className="text-amber-200 font-bold">+{purchase.bonus_coins.toLocaleString()}</span>
                  </div>
                ) : null}
                <div className="border-t border-white/10 pt-2 flex justify-between text-base">
                  <span className="text-white font-bold">New balance</span>
                  <span className="text-amber-300 font-black" data-testid="coin-return-new-balance">
                    🪙 {(wallet?.balance ?? 0).toLocaleString()}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <Link to="/wallet" data-testid="coin-return-to-wallet">
                  <Button className="w-full bg-gradient-to-r from-amber-400 to-pink-500 text-black font-bold hover:from-amber-300 hover:to-pink-400">
                    💰 Back to Wallet
                  </Button>
                </Link>
                <Link to="/home" data-testid="coin-return-to-home">
                  <Button variant="outline" className="w-full text-pink-200 border-pink-500/40 hover:bg-pink-500/10">
                    🏠 Back to Home
                  </Button>
                </Link>
              </div>
              <Link
                to="/wallet#purchases"
                className="inline-block mt-3 text-pink-300/70 hover:text-pink-200 text-xs underline underline-offset-2"
                data-testid="coin-return-history-link"
              >
                View purchase history →
              </Link>
            </Card>
          )}

          {/* 🎁 First-gift nudge — appears AFTER the credited confirmation
              card so the purchase summary stays visible. Renders nothing if
              no suggestion target is available. */}
          {phase === "credited" && <FirstGiftNudge />}

          {phase === "canceled" && (
            <Card className="p-6 sm:p-8 bg-white/5 border-white/10 text-center" data-testid="coin-return-canceled">
              <div className="text-5xl mb-3">↩️</div>
              <h1 className="text-2xl font-black text-white mb-2">Purchase canceled</h1>
              <p className="text-pink-200/70 text-sm mb-5">
                No charges were made. You can try a different pack any time.
              </p>
              <div className="flex flex-col sm:flex-row gap-2">
                <Link to="/wallet" className="flex-1">
                  <Button className="w-full bg-gradient-to-r from-amber-400 to-pink-500 text-black font-bold">
                    💰 Try Again
                  </Button>
                </Link>
                <Link to="/home" className="flex-1">
                  <Button variant="outline" className="w-full text-pink-200 border-pink-500/40">
                    🏠 Back to Home
                  </Button>
                </Link>
              </div>
            </Card>
          )}

          {phase === "timeout" && (
            <Card className="p-6 sm:p-8 bg-amber-500/10 border-amber-400/40 text-center" data-testid="coin-return-timeout">
              <div className="text-5xl mb-3">⏱️</div>
              <h1 className="text-2xl font-black text-white mb-2">Still confirming…</h1>
              <p className="text-amber-100/80 text-sm mb-5">
                Stripe is taking longer than usual. Your coins will appear in your wallet
                automatically — no action needed. You can safely come back later.
              </p>
              <div className="flex flex-col sm:flex-row gap-2">
                <Button
                  onClick={() => navigate(0)}
                  className="flex-1 bg-gradient-to-r from-amber-400 to-pink-500 text-black font-bold"
                  data-testid="coin-return-retry"
                >
                  🔁 Retry
                </Button>
                <Link to="/wallet" className="flex-1">
                  <Button variant="outline" className="w-full text-pink-200 border-pink-500/40">
                    💰 Open Wallet
                  </Button>
                </Link>
              </div>
            </Card>
          )}

          {phase === "error" && (
            <Card className="p-6 sm:p-8 bg-rose-500/10 border-rose-400/40 text-center" data-testid="coin-return-error">
              <div className="text-5xl mb-3">⚠️</div>
              <h1 className="text-2xl font-black text-white mb-2">Couldn't find that purchase</h1>
              <p className="text-rose-100/80 text-sm mb-5">
                The session link looks invalid or expired. If you completed a payment,
                your wallet will still update from the Stripe webhook.
              </p>
              <Link to="/wallet">
                <Button className="bg-gradient-to-r from-amber-400 to-pink-500 text-black font-bold">
                  💰 Open Wallet
                </Button>
              </Link>
            </Card>
          )}
        </div>
      </div>
    </>
  );
};

export default CoinReturnPage;
